import { mobile } from "@btnet/utilities-webview";

import type {
    IDictationRecognizer,
    IDictationSession,
    IDictationSessionHandlers,
} from "utilities/dictation/dictationRecognizer.types";
import { isAndroidWebView } from "utilities/userAgent/userAgent";

/**
 * The Web Speech API implementation of {@link IDictationRecognizer}.
 *
 * BT holds no key and runs no transcription service, but a browser's `SpeechRecognition` is
 * server-side -- Chrome streams audio to Google, Safari to Apple. Accepted for pilot under those
 * terms; see `ncode/epics/task-assistant/289193-dictation-egress/dictation-decision.md`.
 *
 * Inside our mobile app the webview shim replaces `window.SpeechRecognition` with one backed by the
 * device's native engine (BTMobile 114368), so this same code drives the phone's own recognizer.
 * That is why there is no separate native implementation.
 */

// The Web Speech API is not in `lib.dom`, so the shape it is used through is declared here.
interface ISpeechRecognitionAlternative {
    transcript: string;
}

interface ISpeechRecognitionResult {
    readonly isFinal: boolean;
    readonly length: number;
    [index: number]: ISpeechRecognitionAlternative;
}

interface ISpeechRecognitionResultList {
    readonly length: number;
    [index: number]: ISpeechRecognitionResult;
}

interface ISpeechRecognitionEvent {
    readonly resultIndex: number;
    readonly results: ISpeechRecognitionResultList;
}

interface ISpeechRecognitionErrorEvent {
    readonly error: string;
}

interface ISpeechRecognition {
    lang: string;
    continuous: boolean;
    interimResults: boolean;
    start(): void;
    stop(): void;
    abort(): void;
    onresult: ((event: ISpeechRecognitionEvent) => void) | null;
    onerror: ((event: ISpeechRecognitionErrorEvent) => void) | null;
    onend: (() => void) | null;
}

type SpeechRecognitionConstructor = new () => ISpeechRecognition;

declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- augments the DOM's Window
    interface Window {
        SpeechRecognition?: SpeechRecognitionConstructor;
        webkitSpeechRecognition?: SpeechRecognitionConstructor;
        /**
         * Set by the mobile shim once it has replaced the webview's recognizer with a native-backed
         * one (BTMobile 114368). Declared here, not on the webview package's bridge interface,
         * because the shim assigns it straight to `window`.
         */
        supportsNativeSpeechRecognition?: boolean;
    }
}

/**
 * How long a started session waits for its first sign of life.
 *
 * Chrome emits `onend` on silence at ~5-7s, so 5 would false-alarm on a builder who pauses to think;
 * 10 is long enough that they tap again. Liveness only -- whether a recognizer exists at all is
 * answered synchronously by `isAvailable`, and racing a timeout against that is the guesswork the
 * availability probe removed.
 */
const FIRST_SIGNAL_TIMEOUT_MS = 8000;

/** How long a stopped session waits for its recognizer's own end; the mobile bridge waits the same. */
const STOP_SETTLE_TIMEOUT_MS = 3000;

/** The one place the dictation language is decided. Spanish is an open product question (§8.4). */
const DICTATION_LANGUAGE = "en-US";

function getRecognizerConstructor(): SpeechRecognitionConstructor | null {
    if (typeof window === "undefined") {
        return null;
    }

    return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

/**
 * Whether a recognizer here will actually answer -- not merely whether one is defined.
 *
 * A WebView's own `webkitSpeechRecognition` either never fires or, on Android, fires bad transcripts,
 * so a presence check renders a mic that does nothing or types the wrong words. Only BTMobile's shim
 * makes a WebView trustworthy. `mobile.isWebview` sees our app alone (only it injects `sendToNative`),
 * so third-party in-app browsers (Gmail, Outlook, Teams) are caught by the user agent -- on Android
 * only; iOS in-app browsers are not detected here. Deliberately NOT `flag || constructor`: the WebView's
 * constructor is present, so the webview branch has to be able to answer no.
 */
function isBrowserDictationAvailable(): boolean {
    if (getRecognizerConstructor() === null) {
        return false;
    }

    if (mobile.isWebview || isAndroidWebView()) {
        // Absence covers every case the shim declines to install: a non-app origin, an app build
        // predating it, Android 12 and below, and a device with no recognition service.
        return window.supportsNativeSpeechRecognition === true;
    }

    return true;
}

export function createBrowserDictationRecognizer(): IDictationRecognizer {
    return {
        isAvailable: isBrowserDictationAvailable,

        start(handlers: IDictationSessionHandlers): IDictationSession {
            let isWanted = true;
            let hasEnded = false;
            // From `stop()` until the recognizer's own end or an abort: the phrase in flight is the
            // builder's, even if it settles after our deadline has already ended the session.
            let isAwaitingTail = false;

            const Recognizer = getRecognizerConstructor();
            if (!Recognizer) {
                handlers.onError("stopped-unexpectedly");
                handlers.onEnd();
                return { stop: () => {}, abort: () => {} };
            }

            const recognizer = new Recognizer();
            let firstSignalTimer: ReturnType<typeof setTimeout> | undefined;
            let settleTimer: ReturnType<typeof setTimeout> | undefined;

            /**
             * Armed on every start, including the pause-restart below. A restart opens a *new*
             * session, so liveness proven by the previous one does not carry over to it -- without
             * re-arming, a recognizer that dies after its first phrase latches the mic exactly the
             * way an immediately-dead one would.
             */
            function armWatchdog() {
                clearTimeout(firstSignalTimer);
                firstSignalTimer = setTimeout(() => {
                    // Started, then nothing at all: a recognizer that is present and dead. Without
                    // this the caller shows "listening" until the page is reloaded.
                    handlers.onError("stopped-unexpectedly");
                    isWanted = false;
                    recognizer.abort();
                    end();
                }, FIRST_SIGNAL_TIMEOUT_MS);
            }

            armWatchdog();

            function end() {
                if (hasEnded) {
                    return;
                }
                hasEnded = true;
                isWanted = false;
                clearTimeout(firstSignalTimer);
                clearTimeout(settleTimer);
                handlers.onEnd();
            }

            /** Any word from the recognizer proves it is alive, so the watchdog is spent. */
            const noteSignal = () => {
                clearTimeout(firstSignalTimer);
            };

            recognizer.lang = DICTATION_LANGUAGE;
            // The caller owns end-of-turn, so only stop() ends the line.
            recognizer.continuous = true;
            recognizer.interimResults = true;

            recognizer.onresult = (event) => {
                if (hasEnded && !isAwaitingTail) {
                    return;
                }
                noteSignal();

                let pending = "";
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const result = event.results[i];
                    if (result.isFinal) {
                        handlers.onPhrase(result[0].transcript);
                    } else {
                        pending += result[0].transcript;
                    }
                }
                if (!hasEnded) {
                    handlers.onInterim(pending);
                }
            };

            recognizer.onerror = (event) => {
                if (hasEnded) {
                    return;
                }
                noteSignal();

                // Nothing to tell a builder who already stopped, and iOS can still settle the phrase
                // after it -- the recognizer's own end, or the deadline, closes the session.
                if (!isWanted) {
                    return;
                }

                // A pause with no words in it, and a session we aborted ourselves, are both normal.
                // The native engine's own codes arrive already mapped onto these by its shim.
                if (event.error === "no-speech" || event.error === "aborted") {
                    return;
                }
                handlers.onError(
                    event.error === "not-allowed" ? "mic-blocked" : "stopped-unexpectedly",
                );
                end();
            };

            recognizer.onend = () => {
                noteSignal();

                // Chrome ends the session on every pause, so a reopen is not an end -- `onEnd` stays
                // silent, which keeps the caller showing "listening" across a pause.
                if (isWanted) {
                    try {
                        recognizer.start();
                        armWatchdog();
                    } catch {
                        // Already restarting -- the next onend settles it.
                    }
                    return;
                }
                isAwaitingTail = false;
                end();
            };

            try {
                recognizer.start();
            } catch {
                // An already-started recognizer throws rather than reporting, and the shim throws the
                // same `InvalidStateError`. Nothing reaches the callbacks, so this is the one failure
                // the caller only hears about from here.
                handlers.onError("stopped-unexpectedly");
                end();
                return { stop: () => {}, abort: () => {} };
            }

            return {
                stop() {
                    if (!isWanted) {
                        return;
                    }
                    isWanted = false;
                    isAwaitingTail = true;
                    clearTimeout(firstSignalTimer);
                    recognizer.stop();
                    // The recognizer's own `onend` closes the session once the last phrase is out;
                    // the timer covers one that never reports it.
                    settleTimer = setTimeout(end, STOP_SETTLE_TIMEOUT_MS);
                },

                abort() {
                    isAwaitingTail = false;
                    if (hasEnded) {
                        return;
                    }
                    recognizer.abort();
                    end();
                },
            };
        },
    };
}
